import React, { useEffect, useState } from "react";
import {
  FormControl,
  FormLabel,
  HStack,
  IconButton,
  Select,
  Spinner,
  Tooltip,
} from "@chakra-ui/react";
import { HiX } from "react-icons/hi";

const keyOf = (v) => String(v ?? "").trim();

const CodesetSelectCodeWithFilters = ({
  label,
  codesetCode,
  value,          // whole selected object or null
  onChange,       // (obj|null) => void
  filter = null,  // server-side filter string
  size = "md",
  height = undefined,
}) => {
  const [options, setOptions] = useState([]);
  const [allOptions, setAllOptions] = useState([]);
  const [filters, setFilters] = useState([]);
  const [activeFilter, setActiveFilter] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCodeset = async () => {
      setLoading(true);
      try {
        const url = new URL(`https://codex.mrc.ai/api/codelists/${codesetCode}`);
        if (filter) url.searchParams.set("filter", filter);

        const res = await fetch(url.toString());
        const json = await res.json();

        if (Array.isArray(json.codes)) {
          const sorted = [...json.codes].sort((a, b) => {
            const aText = (a.description1 || a.longCode || "").toLowerCase();
            const bText = (b.description1 || b.longCode || "").toLowerCase();
            return aText.localeCompare(bText);
          });

          setAllOptions(sorted);
          setOptions(sorted);

          // build distinct tag filters from space-separated "filter" field
          const allFilterValues = sorted
            .flatMap((item) => (item.filter ? item.filter.split(" ") : []))
            .filter(Boolean);

          const distinctFilters = Array.from(new Set(allFilterValues)).sort();
          setFilters(distinctFilters);
        } else {
          console.error("Unexpected response structure:", codesetCode, json);
          setAllOptions([]);
          setOptions([]);
          setFilters([]);
        }
      } catch (err) {
        console.error("Failed to fetch codeset:", err);
        setAllOptions([]);
        setOptions([]);
        setFilters([]);
      } finally {
        setLoading(false);
      }
    };

    fetchCodeset();
  }, [codesetCode, filter]);

  const handleChange = (e) => {
    const selectedLongCode = keyOf(e.target.value);
    const selected = options.find((item) => keyOf(item.longCode) === selectedLongCode) || null;
    onChange?.(selected); // pass the whole object (or null)
  };

  const handleClear = () => {
    onChange?.(null);
  };

  const TagButton = ({ label, isActive, onClick }) => (
    <Tooltip label={`Filter by ${label}`} aria-label={`${label} filter`}>
      <IconButton
        size={size}
        variant={isActive ? "solid" : "outline"}
        colorScheme={isActive ? "linkedin" : "whiteAlpha"}
        onClick={onClick}
        icon={
          <span
            style={{
              fontSize: "0.6rem",
              fontWeight: "lighter",
              color: isActive ? "white" : undefined,
              padding: "0 4px",
            }}
          >
            {label}
          </span>
        }
        aria-label={label}
      />
    </Tooltip>
  );

  return (
    <FormControl>
      {label && <FormLabel fontSize={size}>{label}</FormLabel>}

      {loading ? (
        <Spinner />
      ) : (
        <>
          <HStack spacing={2}>
            <Select
              placeholder="Select..."
              value={value ? keyOf(value.longCode) : ""} // controlled; ensure string
              onChange={handleChange}
              size={size}
              height={height}
              title={`${value?.description1 || ""} ${value ? keyOf(value.longCode) : ""}`}
            >
              {options.map((item) => {
                const k = keyOf(item.longCode);
                const text = item.description1 || item.longCode || k;
                return (
                  <option key={k} value={k} title={text}>
                    {text}
                  </option>
                );
              })}
            </Select>

            {value?.longCode && (
              <Tooltip label="Clear selection..." aria-label="Clear selection tooltip">
                <IconButton
                  icon={<HiX />}
                  size={size}
                  variant="link"
                  onClick={handleClear}
                  aria-label="Clear"
                />
              </Tooltip>
            )}
          </HStack>

          {filters.length > 0 && (
            <HStack spacing={2} mt={2} wrap="wrap">
              {filters.map((f) => (
                <TagButton
                  key={f}
                  label={f}
                  isActive={activeFilter === f}
                  onClick={() => {
                    setActiveFilter(f);
                    setOptions(
                      allOptions.filter((o) =>
                        (o.filter || "").split(" ").includes(f)
                      )
                    );
                  }}
                />
              ))}

              {activeFilter && (
                <TagButton
                  label="#Remove Filter"
                  isActive={false}
                  onClick={() => {
                    setActiveFilter(null);
                    setOptions(allOptions);
                  }}
                />
              )}
            </HStack>
          )}
        </>
      )}
    </FormControl>
  );
};

export default CodesetSelectCodeWithFilters;
